import type { ReactNode } from "react";
import { ChevronDown, ExternalLink } from "lucide-react";
import { Button } from "@libraryadmin/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@libraryadmin/components/ui/dropdown-menu";
import { Link, useNavigate } from "react-router-dom";
import {
  getLibraryAdminSession,
  logoutLibraryAdminFromServer,
} from "@libraryadmin/services/auth";

interface HeaderProps {
  title?: string;
  subtitle?: string;
  mobileMenuButton?: ReactNode;
}

export function Header({ title, subtitle, mobileMenuButton }: HeaderProps) {
  const navigate = useNavigate();
  const session = getLibraryAdminSession();
  const fullName = [session?.user.firstName, session?.user.lastName]
    .filter(Boolean)
    .join(" ");
  const displayName = fullName || session?.user.email || "Library Admin";

  return (
    <header className="jb-admin-header sticky top-0 z-30 border-b border-border bg-[#fffaf3]/90 backdrop-blur-xl">
      <div className="flex h-14 items-center justify-between px-3 sm:h-[68px] sm:px-6">
        <div className="flex min-w-0 items-center gap-2.5 sm:gap-4">
          {mobileMenuButton}
          {title && (
            <div className="min-w-0">
              <h1 className="truncate text-lg font-semibold text-[#5e2208] sm:text-[22px] md:text-[26px]">
                {title}
              </h1>
              {subtitle && (
                <p className="mt-0.5 hidden truncate text-xs text-muted-foreground sm:block md:text-[13px]">
                  {subtitle}
                </p>
              )}
            </div>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-1.5 sm:gap-3">
          <Link
            to="/"
            target="_blank"
            rel="noreferrer"
            className="hidden items-center gap-1.5 rounded-md border border-[#8a4a2a]/40 bg-[#fffbf5]/70 px-3 py-1.5 text-[13px] font-medium text-[#5e2208] transition-colors hover:bg-[#fffaf3] hover:shadow-sm md:inline-flex"
          >
            View website
            <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
          </Link>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="flex items-center gap-2 rounded-full px-2 hover:bg-muted md:px-3"
              >
                <div className="flex h-9 w-9 items-center justify-center rounded-full border border-[#c8964a]/70 bg-gradient-to-b from-[#8c3c15] to-[#6a280b] text-[13px] font-semibold text-[#fdf1e2] shadow-sm">
                  LA
                </div>
                <div className="hidden text-left md:block">
                  <p className="text-sm font-medium leading-none text-foreground">
                    {displayName}
                  </p>
                  <p className="text-[11px] text-muted-foreground">Librarian</p>
                </div>
                <ChevronDown className="hidden h-4 w-4 text-muted-foreground md:block" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium">{displayName}</p>
                {session?.user.email && fullName && (
                  <p className="text-xs text-muted-foreground">
                    {session.user.email}
                  </p>
                )}
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem>Profile Settings</DropdownMenuItem>
              <DropdownMenuItem>Help & Support</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive"
                onClick={async () => {
                  try {
                    await logoutLibraryAdminFromServer();
                  } finally {
                    navigate("/vault-library-admin", { replace: true });
                  }
                }}
              >
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
